(() => {
  'use strict';

  if (customElements.get('merach-review-picker')) return;

  const instances = new Set();
  const batchSize = 24;
  let instanceCount = 0;

  const normalize = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]/gu, '');

  const create = (tag, className, content) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (content !== undefined) element.textContent = content;
    return element;
  };

  class MerachReviewPicker extends HTMLElement {
    connectedCallback() {
      if (this._listeners) return;
      this._dialog = this.querySelector('dialog');
      this._form = this.querySelector('[data-review-form]');
      this._search = this.querySelector('[data-review-search]');
      this._clear = this.querySelector('[data-review-clear]');
      this._results = this.querySelector('[data-review-results]');
      this._scrollContainer = this._results && (this._results.parentElement || this._results);
      this._status = this.querySelector('[data-review-status]');
      this._empty = this.querySelector('[data-review-empty]');
      this._more = this.querySelector('[data-review-more]');
      this._selection = this.querySelector('[data-review-selection]');
      this._selectedImage = this.querySelector('[data-review-selected-image]');
      this._selectedTitle = this.querySelector('[data-review-selected-title]');
      this._submit = this.querySelector('[data-review-submit]');
      this._error = this.querySelector('[data-review-error]');
      this._retry = this.querySelector('[data-review-retry]');
      if (![this._dialog, this._form, this._search, this._clear, this._results,
        this._status, this._empty, this._more, this._selection,
        this._selectedImage, this._selectedTitle, this._submit,
        this._error, this._retry].every(Boolean)) return;

      this._listeners = new AbortController();
      this._requests = new Set();
      this._life = {};
      this._records = new Map();
      this._loadedPages = new Set();
      this._pages = 1;
      this._pageParam = 'page';
      this._limit = batchSize;
      this._selectedId = '';
      this._submitting = false;
      this._loadingCatalog = false;
      this._catalogError = '';
      this._verificationError = '';
      this._submitToken = 0;
      this._radioName = `merach-review-product-${++instanceCount}`;
      this._submitLabel = this._submit.textContent.trim() || 'Continue to Review';
      const { signal } = this._listeners;

      try {
        this._mergeCatalog(this._readCatalog(this), null);
      } catch (_) {
        this._catalogError = 'Products could not be loaded. Please retry.';
      }

      this._search.addEventListener('input', () => {
        this._limit = batchSize;
        this._scrollContainer.scrollTop = 0;
        this._renderResults();
      }, { signal });
      this._search.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.isComposing) event.preventDefault();
        if (event.key === 'Escape' && !event.isComposing) {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
      }, { signal });
      this._clear.addEventListener('click', () => {
        this._search.value = '';
        this._limit = batchSize;
        this._scrollContainer.scrollTop = 0;
        this._renderResults();
        this._search.focus({ preventScroll: true });
      }, { signal });
      this._results.addEventListener('change', (event) => {
        const radio = event.target;
        if (!(radio instanceof HTMLInputElement) || !radio.matches('[data-review-radio]')) return;
        if (!this._records.has(radio.value)) return;
        this._cancelVerification();
        this._selectedId = radio.value;
        this._verificationError = '';
        this._renderSelection();
        this._syncControls();
      }, { signal });
      this._more.addEventListener('click', () => {
        this._limit += batchSize;
        this._renderResults();
      }, { signal });
      this._retry.addEventListener('click', () => {
        if (this._catalogError) this._loadRemainingPages();
        else if (this._verificationError && this._selectedId) this._submitReview();
      }, { signal });
      this._form.addEventListener('submit', (event) => {
        event.preventDefault();
        this._submitReview();
      }, { signal });
      this.querySelectorAll('[data-merach-review-close]').forEach((button) => {
        button.addEventListener('click', () => this.close(), { signal });
      });
      this._dialog.addEventListener('cancel', () => this._cancelVerification(), { signal });
      this._dialog.addEventListener('close', () => {
        this._cancelVerification();
        if (this._lastOpener && this._lastOpener.isConnected) {
          this._lastOpener.focus({ preventScroll: true });
        }
        this._lastOpener = null;
      }, { signal });
      this._dialog.addEventListener('click', (event) => {
        if (event.target !== this._dialog) return;
        const rect = this._dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right
          || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      }, { signal });

      this._clear.type = 'button';
      this._more.type = 'button';
      this._retry.type = 'button';
      this._results.id ||= `${this._radioName}-results`;
      this._more.setAttribute('aria-controls', this._results.id);
      this._renderResults();
      this._renderSelection();
      instances.add(this);
    }

    disconnectedCallback() {
      instances.delete(this);
      if (this._listeners) this._cancelVerification();
      this._life = null;
      this._submitToken = (this._submitToken || 0) + 1;
      if (this._listeners) this._listeners.abort();
      if (this._requests) this._requests.forEach((controller) => controller.abort());
      this._listeners = null;
      this._verificationController = null;
      this._lastOpener = null;
    }

    open(trigger, keyboard) {
      if (!this.isConnected || !this._listeners || this._dialog.open) return;
      instances.forEach((instance) => {
        if (instance !== this && instance._dialog.open) instance.close();
      });
      this._lastOpener = trigger || document.activeElement;
      this._verificationError = '';
      this._syncControls();
      this._dialog.showModal();
      if (keyboard) this._search.focus({ preventScroll: true });
      this._loadRemainingPages();
    }

    close() {
      this._cancelVerification();
      if (this._dialog.open) this._dialog.close();
    }

    _readCatalog(root) {
      const script = root.querySelector('script[type="application/json"][data-review-catalog]');
      if (!script) throw new Error('Missing product data');
      const catalog = JSON.parse(script.textContent);
      if (!Array.isArray(catalog.products)) throw new Error('Invalid product data');
      const page = Number(catalog.page);
      const pages = Number(catalog.pages);
      if (!Number.isInteger(page) || page < 1 || !Number.isInteger(pages) || pages < 0) {
        throw new Error('Invalid product pagination');
      }
      return { ...catalog, page, pages: Math.max(1, pages) };
    }

    _productUrl(product) {
      const url = new URL(product.url, window.location.origin);
      if (url.origin !== window.location.origin) throw new Error('Invalid product origin');
      const match = url.pathname.match(/^(.*?)\/products\/([^/]+)\/?$/);
      if (!match) throw new Error('Invalid product address');
      // Keep the language prefix while removing collection-specific product paths.
      const prefix = match[1].replace(/\/collections\/[^/]+$/, '');
      const result = new URL(`${prefix}/products/${match[2]}`, window.location.origin);
      return result;
    }

    _mergeCatalog(catalog, requestedPage) {
      if (requestedPage !== null && catalog.page !== requestedPage) {
        throw new Error('Unexpected product page');
      }
      const prepared = catalog.products.map((product, index) => {
        if (typeof product.id !== 'string' || !/^\d+$/.test(product.id)
          || typeof product.title !== 'string' || !product.title.trim()
          || typeof product.url !== 'string') throw new Error('Invalid product');
        const canonical = this._productUrl(product);
        const skus = Array.isArray(product.skus) ? product.skus : [];
        return {
          id: product.id,
          title: product.title,
          handle: String(product.handle || ''),
          url: canonical.toString(),
          image: typeof product.image === 'string' ? product.image : '',
          type: String(product.type || ''),
          order: [catalog.page, index],
          search: normalize([product.title, product.handle, product.type, ...skus].join(' '))
        };
      });
      prepared.forEach((product) => {
        const previous = this._records.get(product.id);
        if (!previous || product.order[0] < previous.order[0]) this._records.set(product.id, product);
      });
      this._pages = Math.max(this._pages, catalog.pages, catalog.page);
      if (typeof catalog.pageParam === 'string' && catalog.pageParam.trim()) {
        this._pageParam = catalog.pageParam;
      }
      this._loadedPages.add(catalog.page);
    }

    async _request(url, format, kind) {
      const controller = new AbortController();
      this._requests.add(controller);
      if (kind === 'verification') this._verificationController = controller;
      const timer = setTimeout(() => controller.abort(), 10000);
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
          headers: { Accept: format === 'json' ? 'application/json' : 'text/html' }
        });
        if (!response.ok) {
          const error = new Error(`Request failed (${response.status})`);
          error.status = response.status;
          throw error;
        }
        const text = await response.text();
        return format === 'json' ? JSON.parse(text) : text;
      } finally {
        clearTimeout(timer);
        this._requests.delete(controller);
        if (this._verificationController === controller) this._verificationController = null;
      }
    }

    async _loadRemainingPages() {
      if (this._loadingCatalog || !this.isConnected) return;
      if (this._loadedPages.size >= this._pages && !this._catalogError) return;
      const life = this._life;
      this._loadingCatalog = true;
      this._catalogError = '';
      this._renderResults();
      try {
        for (let page = 1; page <= this._pages; page += 1) {
          if (this._loadedPages.has(page)) continue;
          const url = new URL(window.location.href);
          url.hash = '';
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set(this._pageParam, String(page));
          const html = await this._request(url, 'text', 'catalog');
          if (!this.isConnected || this._life !== life) return;
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const root = Array.from(doc.querySelectorAll('merach-review-picker'))
            .find((element) => element.dataset.sectionId === this.dataset.sectionId);
          if (!root) throw new Error('Missing product selector');
          this._mergeCatalog(this._readCatalog(root), page);
          this._renderResults();
          this._renderSelection();
        }
      } catch (_) {
        if (!this.isConnected || this._life !== life) return;
        this._catalogError = 'Some products could not be loaded. Retry to search all products.';
      } finally {
        if (this.isConnected && this._life === life) {
          this._loadingCatalog = false;
          this._renderResults();
        }
      }
    }

    _matchingProducts() {
      const terms = this._search.value.trim().split(/\s+/).map(normalize).filter(Boolean);
      return Array.from(this._records.values())
        .sort((a, b) => a.order[0] - b.order[0] || a.order[1] - b.order[1])
        .filter((product) => terms.every((term) => product.search.includes(term)));
    }

    _placeholder() {
      const placeholder = create('span', 'mr-review-picker__placeholder', 'M');
      placeholder.setAttribute('aria-hidden', 'true');
      return placeholder;
    }

    _productCard(product) {
      const label = create('label', 'mr-review-picker__card');
      label.classList.toggle('is-selected', product.id === this._selectedId);
      const radio = create('input', 'mr-review-picker__radio');
      radio.type = 'radio';
      radio.name = this._radioName;
      radio.value = product.id;
      radio.dataset.reviewRadio = '';
      radio.checked = product.id === this._selectedId;
      label.append(radio);
      if (product.image) {
        const image = create('img', 'mr-review-picker__image');
        image.src = product.image;
        image.alt = '';
        image.width = 64;
        image.height = 64;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.addEventListener('error', () => image.replaceWith(this._placeholder()), { once: true });
        label.append(image);
      } else label.append(this._placeholder());
      const info = create('span', 'mr-review-picker__product-info');
      info.append(create('span', 'mr-review-picker__product-title', product.title));
      if (product.type) info.append(create('span', 'mr-review-picker__product-type', product.type));
      label.append(info);
      const check = create('span', 'mr-review-picker__check', '✓');
      check.setAttribute('aria-hidden', 'true');
      label.append(check);
      return label;
    }

    _renderResults() {
      const products = this._matchingProducts();
      const shown = products.slice(0, this._limit);
      const active = document.activeElement;
      const activeId = active instanceof HTMLInputElement && this._results.contains(active)
        ? active.value : '';
      const scrollTop = this._scrollContainer.scrollTop;
      Array.from(this._results.children).forEach((child) => {
        if (child.tagName !== 'LEGEND') child.remove();
      });
      const fragment = document.createDocumentFragment();
      shown.forEach((product) => fragment.append(this._productCard(product)));
      this._results.append(fragment);
      this._scrollContainer.scrollTop = scrollTop;
      if (activeId) {
        const replacement = Array.from(this._results.querySelectorAll('[data-review-radio]'))
          .find((radio) => radio.value === activeId);
        if (replacement) replacement.focus({ preventScroll: true });
      }
      this._clear.hidden = this._search.value.length === 0;
      this._more.hidden = shown.length >= products.length;
      this._more.textContent = 'Show more products';
      this._empty.hidden = products.length > 0;
      if (this._loadingCatalog) this._empty.textContent = 'Looking for more products…';
      else if (this._catalogError) this._empty.textContent = 'The full product list is not available yet. Please retry.';
      else if (this._search.value.trim()) this._empty.textContent = 'No matching products. Try a product name, model, or SKU.';
      else this._empty.textContent = 'No products are available to review here yet.';
      const count = this._search.value.trim() ? 'matching products' : 'products';
      this._status.textContent = `Showing ${shown.length} of ${products.length} ${count}.`
        + (this._loadingCatalog ? ' Loading more products…' : '')
        + (this._catalogError ? ' The product list is incomplete.' : '');
      this._syncControls();
    }

    _renderSelection() {
      const product = this._records.get(this._selectedId);
      this._selection.hidden = !product;
      this._selectedTitle.textContent = product ? product.title : '';
      this._selectedImage.onerror = null;
      if (product && product.image) {
        this._selectedImage.hidden = false;
        this._selectedImage.alt = '';
        this._selectedImage.src = product.image;
        this._selectedImage.onerror = () => { this._selectedImage.hidden = true; };
      } else {
        this._selectedImage.hidden = true;
        this._selectedImage.removeAttribute('src');
      }
      this._results.querySelectorAll('[data-review-radio]').forEach((radio) => {
        radio.checked = radio.value === this._selectedId;
        radio.closest('label').classList.toggle('is-selected', radio.checked);
      });
    }

    _syncControls() {
      this._submit.disabled = !this._records.has(this._selectedId) || this._submitting;
      this._submit.textContent = this._submitting ? 'Opening review…' : this._submitLabel;
      this._submit.setAttribute('aria-busy', String(this._submitting));
      const message = [this._verificationError, this._catalogError].filter(Boolean).join(' ');
      this._error.hidden = !message;
      this._error.textContent = message;
      this._retry.hidden = !message || this._loadingCatalog || this._submitting;
      this._retry.textContent = this._catalogError ? 'Try loading products again' : 'Try again';
    }

    _cancelVerification() {
      this._submitToken += 1;
      if (this._verificationController) this._verificationController.abort();
      this._submitting = false;
      this._syncControls();
    }

    async _submitReview() {
      if (this._submitting || !this._dialog.open) return;
      const product = this._records.get(this._selectedId);
      if (!product) {
        this._search.focus({ preventScroll: true });
        return;
      }
      const token = ++this._submitToken;
      const life = this._life;
      this._submitting = true;
      this._verificationError = '';
      this._syncControls();
      let navigated = false;
      try {
        const destination = this._productUrl(product);
        const lookup = new URL(destination);
        lookup.pathname += '.js';
        const currentProduct = await this._request(lookup, 'json', 'verification');
        if (!this.isConnected || this._life !== life || token !== this._submitToken
          || !this._dialog.open || this._selectedId !== product.id) return;
        if (String(currentProduct.id) !== product.id) {
          this._verificationError = 'This product could not be matched safely. Please choose it again or retry.';
          return;
        }
        destination.searchParams.set('ref', 'review');
        const currentUrl = new URL(window.location.href);
        if (currentUrl.searchParams.has('preview_theme_id')) {
          destination.searchParams.set('preview_theme_id', currentUrl.searchParams.get('preview_theme_id'));
        }
        window.location.assign(destination.toString());
        navigated = true;
      } catch (error) {
        if (!this.isConnected || this._life !== life || token !== this._submitToken) return;
        this._verificationError = error.status === 404
          ? 'This product could not be found. Please choose another product.'
          : 'We could not confirm this product. Please check your connection and retry, or choose another product.';
      } finally {
        if (this.isConnected && this._life === life && token === this._submitToken) {
          this._submitting = navigated;
          this._syncControls();
        }
      }
    }
  }

  customElements.define('merach-review-picker', MerachReviewPicker);

  // One delegated listener also covers buttons inserted by the theme editor.
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('[data-merach-review-open]');
    if (!trigger) return;
    const scope = trigger.closest('[id^="shopify-section-"]');
    const local = trigger.closest('merach-review-picker')
      || (scope && scope.querySelector('merach-review-picker'));
    const picker = local && instances.has(local) ? local
      : Array.from(instances).find((instance) => instance.isConnected);
    if (!picker) return;
    event.preventDefault();
    picker.open(trigger, event.detail === 0);
  });
})();
